import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Radio, User, LogOut, Settings, Crown, Trophy, Clock, Users, Shield, Coins } from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full avatar URL
const getAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (avatar.startsWith('/api/')) return `${API_URL}${avatar}`;
  return avatar;
};

export const Header = () => {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [canStream, setCanStream] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);

  const checkCanStream = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/streams/can-stream`, {
        headers: getAuthHeader()
      });
      setCanStream(response.data.can_stream);
      if (response.data.time_remaining !== undefined) {
        setTimeRemaining(response.data.time_remaining);
      }
    } catch (error) {
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (user && user.user_type === 'streamer') {
      checkCanStream();
    }
  }, [user, checkCanStream]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatTimeShort = (seconds) => {
    if (seconds <= 0) return '0min';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h${mins}m`;
    return `${mins}min`;
  };

  return (
    <header className="header">
      <div className="flex items-center justify-between px-4 md:px-8 py-4">
        {/* Logo */}
        <Link 
          to="/" 
          className="flex items-center gap-3 group"
          data-testid="logo-link"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#D946EF] flex items-center justify-center group-hover:scale-110 transition-transform">
            <Radio className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold tracking-tight hidden sm:block">
            <span className="gradient-text">Tu Liga</span>
            <span className="text-white"> Online</span>
          </span>
        </Link>

        {/* Center Links - Visible on all screens */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          <Link 
            to="/streamers" 
            className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#7C3AED]/10 hover:bg-[#7C3AED]/20 text-[#7C3AED] transition-colors"
            data-testid="streamers-link"
          >
            <Users className="w-4 h-4" />
            <span className="font-medium text-xs sm:text-sm hidden xs:inline">Streamers</span>
          </Link>
          <Link 
            to="/ranking" 
            className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-500 transition-colors"
            data-testid="ranking-link"
          >
            <Trophy className="w-4 h-4" />
            <span className="font-medium text-xs sm:text-sm hidden xs:inline">Ranking</span>
          </Link>
          <Link 
            to="/marsucoins" 
            className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 text-yellow-400 transition-colors border border-yellow-500/20"
            data-testid="marsucoins-link"
          >
            <Coins className="w-4 h-4" />
            <span className="font-medium text-xs sm:text-sm hidden xs:inline">MarsuCoins</span>
          </Link>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-3">
          {user ? (
            <>
              {/* Transmitir button - only for streamers */}
              {user.user_type === 'streamer' && (
                <>
                  {canStream ? (
                    <Button 
                      asChild
                      className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white rounded-full px-6 font-semibold btn-glow"
                      data-testid="go-live-btn"
                    >
                      <Link to="/go-live">
                        <Radio className="w-4 h-4 mr-2" />
                        Transmitir
                        {user.subscription_type !== 'pro' && timeRemaining !== null && (
                          <span className="ml-2 text-xs opacity-75">
                            ({formatTimeShort(timeRemaining)})
                          </span>
                        )}
                      </Link>
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button 
                        asChild
                        className="bg-green-600 hover:bg-green-700 text-white rounded-full px-4 font-semibold"
                        data-testid="buy-time-btn"
                      >
                        <Link to="/go-live">
                          <Clock className="w-4 h-4 mr-1" />
                          Comprar
                        </Link>
                      </Button>
                      <span className="bg-gray-700 text-gray-400 rounded-full px-4 py-2 font-semibold text-sm cursor-not-allowed">
                        Sin tiempo
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Notification Bell */}
              <NotificationBell />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 p-1 rounded-full hover:bg-white/5 transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <Avatar className="w-9 h-9 border-2 border-[#7C3AED]/50">
                      <AvatarImage src={getAvatarUrl(user.avatar)} alt={user.username} />
                      <AvatarFallback className="bg-[#7C3AED]/20 text-white">
                        {user.username?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-56 bg-[#0A0A0A] border-white/10"
                >
                  <div className="px-3 py-2">
                    <p className="font-semibold text-white">{user.username}</p>
                    <p className="text-sm text-gray-400">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-white/5"
                    onClick={() => navigate(`/profile/${user.username}`)}
                    data-testid="profile-menu-item"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Mi Perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-white/5"
                    onClick={() => navigate('/settings')}
                    data-testid="settings-menu-item"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configuración
                  </DropdownMenuItem>
                  {user.email === 'admin@tuliga.online' && (
                    <DropdownMenuItem 
                      className="cursor-pointer hover:bg-[#7C3AED]/10 text-[#7C3AED]"
                      onClick={() => navigate('/admin')}
                      data-testid="admin-menu-item"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Panel Admin
                    </DropdownMenuItem>
                  )}
                  {user.subscription_type !== 'pro' && (
                    <DropdownMenuItem 
                      className="cursor-pointer hover:bg-yellow-500/10 text-yellow-500"
                      onClick={() => navigate('/pro')}
                      data-testid="pro-menu-item"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Hazte PRO
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-white/5 text-red-400"
                    onClick={handleLogout}
                    data-testid="logout-menu-item"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <Button 
                asChild
                variant="ghost" 
                className="text-gray-300 hover:text-white hover:bg-white/5 rounded-full"
                data-testid="login-btn"
              >
                <Link to="/login">Iniciar Sesión</Link>
              </Button>
              <Button 
                asChild
                className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white rounded-full px-6 font-semibold btn-glow"
                data-testid="register-btn"
              >
                <Link to="/register">Registrarse</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
